/**
 * MomentsPageDetailPage — the owner's single-page edit + gallery view.
 *
 * Tabs:
 *   1. Gallery       — moderate approved / pending uploads
 *   2. Share & QR    — QR / Wallet / NFC / Web Share (MomentsShareBar)
 *   3. Settings      — rename, theme, auto-approve, allow-video, pause
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Tv, Image as ImageIcon,
  ClipboardList, Settings2, ExternalLink, PauseCircle, PlayCircle,
} from "lucide-react";
import MomentsShareBar from "@/components/MomentsShareBar";
import MomentsGallery from "@/components/MomentsGallery";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

const TABS = [
  { key: "gallery",  label: "Gallery",  icon: ImageIcon },
  { key: "pending",  label: "Pending",  icon: ClipboardList },
  { key: "share",   label: "Share & QR", icon: Tv },
  { key: "settings", label: "Settings", icon: Settings2 },
];

export default function MomentsPageDetailPage() {
  const { pageId } = useParams();
  const nav = useNavigate();
  const [page,    setPage]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [tab,     setTab]     = useState("gallery");
  const [galleryVersion, setGalleryVersion] = useState(0);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/moments/pages/${pageId}`, {
        credentials: "include",
      });
      if (r.status === 401) { nav("/login"); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setPage(j);
    } catch (e) {
      toast.error(`Load failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [pageId]);

  const _patch = async (updates) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/moments/pages/${pageId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setPage(j);
      toast.success("Saved");
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const _moderate = async (mediaId, status) => {
    const r = await fetch(`${API}/api/moments/media/${mediaId}/moderate`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      throw new Error(j.detail || `HTTP ${r.status}`);
    }
    toast.success(status === "approved" ? "Approved" : "Rejected");
    setGalleryVersion((v) => v + 1);
  };

  const _deleteMedia = async (mediaId) => {
    const r = await fetch(`${API}/api/moments/media/${mediaId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    toast.success("Deleted");
    setGalleryVersion((v) => v + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-stone-400" size={22}/>
      </div>
    );
  }

  if (!page) return null;

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100"
         data-testid="moments-detail-root">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link
          to="/moments/dashboard"
          data-testid="moments-detail-back"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900 dark:hover:text-white mb-6"
        >
          <ArrowLeft size={14}/> All Moment Pages
        </Link>

        <header className="flex items-start gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase"
               style={{ color: page.accent_hex }}>
              {page.theme}
            </p>
            <h1 className="text-3xl sm:text-4xl font-black">{page.title}</h1>
            {page.location && (
              <p className="text-sm text-stone-500 mt-1">{page.location}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => _patch({ active: !page.active })}
              disabled={saving}
              data-testid="moments-detail-toggle-active"
              className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 h-9 border border-stone-300 hover:border-stone-500 dark:border-stone-700"
            >
              {page.active
                ? <><PauseCircle size={14}/> Pause guest uploads</>
                : <><PlayCircle size={14}/> Resume</>}
            </button>
            <a
              href={`/m/${page.slug}/live`}
              target="_blank" rel="noreferrer"
              data-testid="moments-detail-live-btn"
              className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 h-9 bg-stone-900 text-white hover:bg-stone-700"
            >
              <Tv size={14}/> Live slideshow <ExternalLink size={12}/>
            </a>
          </div>
        </header>

        {/* Tabs */}
        <nav className="mt-8 border-b border-stone-200 dark:border-stone-800 flex gap-1 overflow-x-auto"
             data-testid="moments-detail-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`moments-detail-tab-${key}`}
              className={`inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold border-b-2 transition ${
                tab === key
                  ? "border-pink-600 text-pink-600"
                  : "border-transparent text-stone-500 hover:text-stone-900 dark:hover:text-white"
              }`}
            >
              <Icon size={14}/> {label}
            </button>
          ))}
        </nav>

        <section className="mt-6">
          {tab === "gallery" && (
            <MomentsGallery
              slug={page.slug}
              isOwner
              filter="approved"
              moderateFn={_moderate}
              deleteFn={_deleteMedia}
              refreshKey={galleryVersion}
              testid="moments-owner-gallery"
            />
          )}
          {tab === "pending" && (
            <MomentsGallery
              slug={page.slug}
              isOwner
              filter="pending"
              moderateFn={_moderate}
              deleteFn={_deleteMedia}
              refreshKey={galleryVersion}
              testid="moments-owner-pending"
            />
          )}
          {tab === "share" && (
            <MomentsShareBar
              slug={page.slug}
              guestUrl={page.guest_url}
              title={page.title}
              accentHex={page.accent_hex}
            />
          )}
          {tab === "settings" && (
            <SettingsPanel page={page} saving={saving} onPatch={_patch}/>
          )}
        </section>
      </div>
    </div>
  );
}

function SettingsPanel({ page, saving, onPatch }) {
  const [draft, setDraft] = useState({
    title:          page.title,
    description:    page.description || "",
    location:       page.location || "",
    accent_hex:     page.accent_hex || "#f59e0b",
    auto_approve:   !!page.auto_approve,
    allow_video:    page.allow_video !== false,
    allow_captions: page.allow_captions !== false,
  });

  return (
    <div className="rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-5 space-y-4"
         data-testid="moments-settings">
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs uppercase font-bold text-stone-500 tracking-wider">Title</span>
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            data-testid="moments-settings-title"
            className="mt-1 w-full h-11 rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase font-bold text-stone-500 tracking-wider">Location</span>
          <input
            value={draft.location}
            onChange={(e) => setDraft({ ...draft, location: e.target.value })}
            data-testid="moments-settings-location"
            className="mt-1 w-full h-11 rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs uppercase font-bold text-stone-500 tracking-wider">Description</span>
          <textarea
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={2}
            data-testid="moments-settings-description"
            className="mt-1 w-full rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-3 py-2 resize-none"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase font-bold text-stone-500 tracking-wider">Accent colour</span>
          <input
            type="color"
            value={draft.accent_hex}
            onChange={(e) => setDraft({ ...draft, accent_hex: e.target.value })}
            data-testid="moments-settings-accent"
            className="mt-1 h-11 w-full rounded-xl border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 px-2 py-1"
          />
        </label>
      </div>

      <div className="space-y-2 border-t border-stone-100 dark:border-stone-800 pt-4">
        {[
          ["auto_approve",   "Auto-publish uploads (skip moderation queue)"],
          ["allow_video",    "Allow video uploads (25 MB max per file)"],
          ["allow_captions", "Let guests add captions to their uploads"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!draft[k]}
              onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })}
              data-testid={`moments-settings-${k}`}
              className="w-4 h-4 accent-pink-600"
            />
            <span className="text-sm">{label}</span>
          </label>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onPatch(draft)}
        disabled={saving}
        data-testid="moments-settings-save"
        className="inline-flex items-center gap-2 rounded-full bg-pink-600 hover:bg-pink-500 text-white px-5 h-11 font-semibold disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
        Save changes
      </button>
    </div>
  );
}
